import React, { createContext, useState, useContext, useEffect } from 'react';
import { setAuthToken, getAuthToken, clearAuthToken } from '../utils/auth';

interface User {
  id: number;
  username: string;
  is_staff: boolean;
  has_completed_intake?: boolean; // Now optional since we use the flow completion endpoint
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  isAuthLoading: boolean; // Add loading state
  login: (token: string, userData: User) => void;
  logout: () => void;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true); // Initialize loading state

  useEffect(() => {
    const attemptLoginFromSession = async () => {
      try {
        // The browser automatically sends the session cookie from social login.
        // Our backend's CustomAuthToken view exchanges this session for a DRF API token.
        const response = await fetch('http://localhost:8000/api/token/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // No 'Authorization' header is sent; we rely on the session cookie.
          },
          credentials: 'include', // <-- IMPORTANT: This tells the browser to send cookies.
        });

        if (response.ok) {
          const data = await response.json();
          if (data.token && data.user) {
            console.log('AuthContext: Successfully exchanged session for API token.');
            login(data.token, data.user); // Use existing login function to set state
          }
        } else {
          // This is the expected outcome if there's no active session.
          // console.log('AuthContext: No active session found to exchange for a token.');
        }
      } catch (error) {
        console.error('AuthContext: Error attempting to login from session:', error);
      } finally {
        setIsAuthLoading(false); // Stop loading after session check completes
      }
    };

    const token = getAuthToken();
    const userDataString = localStorage.getItem('user');

    if (token && userDataString) {
      // If we already have a token in localStorage, trust it and load the user.
      try {
        const userData = JSON.parse(userDataString);
        setUser(userData);
        setIsAuthLoading(false); // Stop loading if token is found
      } catch (error) {
        console.error('AuthContext: Error parsing auth data from localStorage:', error);
        clearAuthToken();
        localStorage.removeItem('user');
        setIsAuthLoading(false); // Stop loading on error too
      }
    } else {
      // If no token is found, try to get one using the session cookie.
      // This is the key step for the post-social-login flow.
      attemptLoginFromSession();
    }
  }, []); // The dependency array is empty, so this runs once on mount.

  const login = (token: string, userData: User) => {
    setAuthToken(token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    clearAuthToken();
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    }
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated: !!user,
      user,
      isAuthLoading, // Expose loading state
      login,
      logout,
      updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
